import React from "react";

export default function RetirementCalculatorHelpContent() {
  return (
    <div style={{ lineHeight: 1.7, color: "#243041", fontSize: 14 }}>
      <p>Esta calculadora te ajuda a estimar:</p>
      <ul>
        <li>O seu “número” da aposentadoria (quanto você pode precisar ter investido)</li>
        <li>Quanto investir por mês para chegar nesse número até a data em que você quer se aposentar</li>
      </ul>
      <p>Ela é uma ferramenta de planejamento — um ponto de partida para definir metas claras.</p>

      <h4 style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, marginTop: 18 }}>
        O que a calculadora calcula
      </h4>

      <p>
        <strong>A) Patrimônio-alvo (“Seu número”)</strong><br />
        A calculadora estima o tamanho da carteira necessário para sustentar o seu gasto na aposentadoria usando
        cenários de taxa de saque (4%, 3,5% e 3%).
      </p>

      <p>
        <strong>B) Aporte mensal estimado</strong><br />
        Se você informar quanto já tem investido, quantos anos faltam para se aposentar e o retorno esperado,
        a calculadora estima o aporte mensal necessário para atingir o patrimônio-alvo.
      </p>

      <h4 style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, marginTop: 18 }}>
        Entradas (inputs) — explicação de cada campo
      </h4>

      <p>
        <strong>1) Gasto mensal desejado na aposentadoria (USD)</strong><br />
        O que é: quanto você quer gastar por mês quando estiver aposentado(a).<br />
        Como a calculadora usa: converte para gasto anual: <strong>Gasto anual = gasto mensal × 12</strong><br />
        Dica: inclua moradia, alimentação, transporte, saúde, lazer/viagens e “extras”.
      </p>

      <p>
        <strong>2) Contar Social Security? (Sim/Não)</strong><br />
        Se Sim, a calculadora considera que parte do seu gasto será coberta pelo Social Security e reduz quanto você
        precisa retirar dos investimentos.
      </p>

      <p>
        <strong>3) Social Security mensal estimado (USD)</strong><br />
        O que é: o valor estimado do benefício mensal (se você optar por incluir).<br />
        Como a calculadora usa: converte para anual: <strong>Social Security anual = Social Security mensal × 12</strong><br />
        Atenção: o valor real depende do seu histórico de salários e da idade em que você solicita o benefício.
        Se você não tiver certeza, pode colocar <strong>0</strong> e planejar de forma mais conservadora.
      </p>

      <p>
        <strong>4) Quanto você já tem investido hoje (USD)</strong><br />
        O que é: o total que você já acumulou e que vai servir para a meta.
      </p>

      <p>
        <strong>5) Anos até se aposentar</strong><br />
        O que é: quantos anos faltam até a data em que você quer se aposentar.
      </p>

      <p>
        <strong>6) Retorno anual esperado — padrão: 6%</strong><br />
        O que é: a taxa média anual de crescimento que você assume para seus investimentos durante o período em que
        você ainda está aportando (fase de acumulação).<br />
        <strong>Neste campo, o valor é em decimal:</strong> <strong>0.06 = 6%</strong>, <strong>0.04 = 4%</strong>,{" "}
        <strong>0.07 = 7%</strong>.<br />
        Importante: retorno é uma suposição para planejamento. Na prática, ele varia muito de ano para ano.
      </p>

      <h4 style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, marginTop: 18 }}>
        Resultados (outputs) — como interpretar
      </h4>

      <p><strong>1) Gasto anual</strong><br />Seu gasto mensal convertido em gasto por ano.</p>

      <p><strong>2) Renda garantida anual</strong><br />
        Se você marcou Social Security como “Sim”, aqui aparece o valor anual estimado do benefício.
      </p>

      <p><strong>3) “Sai da carteira (gap)”</strong><br />
        É o quanto seus investimentos precisam pagar por ano:<br />
        <strong>Gap anual = max(0, gasto anual − renda garantida anual)</strong><br />
        Se o gap der zero, significa que sua renda garantida cobre o gasto informado (não é comum, mas pode acontecer).
      </p>

      <h4 style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, marginTop: 18 }}>
        Cenários de taxa de saque (4%, 3,5%, 3%)
      </h4>

      <p>
        A calculadora mostra três cenários porque não existe uma taxa “perfeita” para todo mundo:<br />
        • 4%: atalho mais conhecido para planejamento<br />
        • 3,5%: mais conservador<br />
        • 3%: bem conservador (útil para aposentadoria muito longa ou aposentadoria antecipada)
      </p>

      <p>
        <strong>Fórmula do patrimônio-alvo</strong><br />
        Para cada cenário:<br />
        <strong>Patrimônio-alvo = Gap anual ÷ taxa de saque</strong><br />
        Exemplos:<br />
        • 4%: divide por 0,04 (≈ multiplicar por 25)<br />
        • 3,5%: divide por 0,035 (≈ 28,6×)<br />
        • 3%: divide por 0,03 (≈ 33,3×)
      </p>

      <h4 style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, marginTop: 18 }}>
        Como o “aporte mensal estimado” é calculado
      </h4>

      <p>
        Quando você informa quanto já tem investido, anos até se aposentar e retorno anual esperado (padrão 6%),
        a calculadora estima o aporte mensal necessário usando juros compostos.
        <br /><br />
        Se aparecer <strong>US$ 0/mês</strong>, isso significa que, com as suposições atuais, o que você já tem investido
        seria suficiente para chegar ao alvo.
      </p>

      <h4 style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, marginTop: 18 }}>
        Limitações importantes
      </h4>

      <p>
        Esta calculadora é uma estimativa educacional e não inclui: impostos, custos de saúde, taxas de investimento,
        volatilidade do mercado/“sequência de retornos”, mudanças de estilo de vida, emergências e ajustes detalhados
        de inflação. Use como ponto de partida e refine depois conforme sua realidade.
      </p>
    </div>
  );
}
